import type {
  OmBannerItem,
  ProductUnitItem,
  RelatedKeywordItem,
  SectionDividerItem,
  SectionGroupHeaderItem,
  RecommendedListHeader,
  UniversalSearchRequestBody,
  UniversalSearchHeaderRequestBody,
} from '@weberus/shared-types';

import { useAtomValue } from 'jotai';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useInView } from 'react-intersection-observer';
import { useEffect } from 'react';
import { noop } from 'lodash-es';
import { queries } from '@/app/_shared/queries';
import { localLocationAtom } from '@/app/_shared/store/location';
import {
  globalFilterParamsAtom,
  searchFilterKeywordAtom,
  searchFilterSortAtom,
  tempGlobalFilterParamsAtom,
} from '@/app/_shared/store/search-filter';
import { hashedMemberNoAtom } from '@/app/_shared/store/member';
import {
  capacityAdultAtom,
  capacityChildAgesAtom,
  checkInDateAtom,
  checkOutDateAtom,
} from '@/app/_shared/store/local-accommodation';
import { selectedVerticalTabInfoAtom, universalHeaderReqBodyAtom } from '@/app/_shared/store/universal-search';
import { getPageKey } from '@/app/(pages)/results/utils/get-page-key';
import { globalCheckIOCapacityAtom } from '@/app/_shared/store/global-accommodation';

export type GroupedItems =
  | ProductUnitItem[]
  | OmBannerItem
  | RelatedKeywordItem
  | SectionGroupHeaderItem
  | SectionDividerItem;

/** 버티컬탭 숙소 검색 리스트 조회 */
interface BaseSearchQueryParams {
  headers?: Record<string, string>;
  params: UniversalSearchRequestBody;
  hashedMemberNo: string | undefined;
  pageKey: string | undefined;
  enabled: boolean;
}

interface SearchListQueryParams {
  headers?: Record<string, string>;
  category: string;
  params: UniversalSearchRequestBody;
  hashedMemberNo: string | undefined;
}

/** 통합 검색 헤더 정보 조회 */
export const useUniversalSearchHeaderQuery = () => {
  const searchRequestParams = useAtomValue(universalHeaderReqBodyAtom);
  return useQuery({
    ...queries.list.getUniversalSearchHeader(searchRequestParams),
    placeholderData: keepPreviousData, // 이전 데이터 유지
  });
};

/** 통합 검색 CTA 버튼 개수 조회 */
export const useUniversalSearchCountQuery = (enabled = true) => {
  const searchRequestParams = useAtomValue(universalHeaderReqBodyAtom);
  const tempGlobalFilterParams = useAtomValue(tempGlobalFilterParamsAtom);

  return useQuery({
    ...queries.list.getUniversalSearchCount({ ...searchRequestParams, filters: tempGlobalFilterParams }),
    enabled,
    placeholderData: keepPreviousData, // 이전 데이터 유지
  });
};

/** 국내 숙소 지도 조회 */
export const useUniversalLocalMapQuery = () => {
  const checkInDate = useAtomValue(checkInDateAtom);
  const checkOutDate = useAtomValue(checkOutDateAtom);
  const capacityAdults = useAtomValue(capacityAdultAtom);
  const childrenAges = useAtomValue(capacityChildAgesAtom);
  const globalCheckIOCapacity = useAtomValue(globalCheckIOCapacityAtom);
  const hashedMemberNo = useAtomValue(hashedMemberNoAtom);
  const sort = useAtomValue(searchFilterSortAtom);
  const location = useAtomValue(localLocationAtom);
  const keyword = useAtomValue(searchFilterKeywordAtom);
  const filters = useAtomValue(globalFilterParamsAtom);
  const selectedTabInfo = useAtomValue(selectedVerticalTabInfoAtom);

  const params = {
    keyword,
    filters: filters || [],
    category: selectedTabInfo?.key,
    sort: sort || 'RECOMMEND',
    userLocation: {
      latitude: Number(location?.latitude),
      longitude: Number(location?.longitude),
      locationType: location?.locationType,
      locationTime: location?.locationTime,
    },
    localAccommodation: {
      checkInDate,
      checkOutDate,
      capacityAdults,
      childrenAges,
    },
    globalAccommodation: {
      checkInDate: globalCheckIOCapacity.checkInDate,
      checkOutDate: globalCheckIOCapacity.checkOutDate,
      rooms: globalCheckIOCapacity.capacityRooms.map((room) => ({
        capacityAdults: room.adult,
        childrenAges: room.child,
      })),
    },
  } as UniversalSearchHeaderRequestBody;

  const { queryKey, queryFn } = queries.list.getUniversalSearchMap(params, hashedMemberNo);

  const result = useInfiniteQuery({
    queryKey,
    queryFn,
    enabled: !!keyword,
    initialPageParam: 1,
    getNextPageParam: (params) => (!params?.paging?.isLast ? (params?.paging?.current ?? 1) + 1 : undefined),
    gcTime: 1000 * 60 * 30, //30분 후 GC
    staleTime: 1000 * 60 * 30, //30분 후 캐시 만료
    // meta: { persist: true }, // 데이터 복원이 필요없을것 같아서 임시 주석 처리
  });

  const { data, fetchNextPage, isLoading, isFetchingNextPage, isRefetching, isFetchNextPageError } = result;
  const isDataLoading = isLoading || isFetchingNextPage || isRefetching;
  const currentPaging = data?.pages[data.pages.length - 1]?.paging;
  const placeList = data?.pages?.flatMap((page) => page?.items ?? []) ?? [];

  return {
    ...result,
    paging: currentPaging,
    isDataLoading,
    placeList,
    fetchNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    serverLogMeta: data?.pages[0]?.serverLogMeta || {},
    isFirstPageLoaded: !!data?.pages[0],
  };
};

export const useSearchListQuery = ({ headers, category, params, hashedMemberNo }: SearchListQueryParams) => {
  const isLocal = ['LOCAL_ACCOMMODATION', 'LOCAL_LEISURE', 'ENTERTAINMENT'].includes(category);
  const isGlobal = category === 'GLOBAL_ACCOMMODATION';

  const queryParams = { params, hashedMemberNo, pageKey: getPageKey() };
  const localQueryResult = useUniversalSearchListQuery({ ...queryParams, enabled: isLocal, headers });
  const globalQueryResult = useGlobalSearchListQuery({ ...queryParams, enabled: isGlobal, headers });

  return isLocal ? localQueryResult : globalQueryResult;
};

export const useUniversalSearchListQuery = ({
  headers,
  params,
  hashedMemberNo,
  pageKey,
  enabled,
}: BaseSearchQueryParams) => {
  const { ref, inView } = useInView({ rootMargin: '500px 0px' });
  const category = params?.category || 'LOCAL_ACCOMMODATION';
  const { queryKey, queryFn } = queries.list.getUniversalSearch(
    params,
    hashedMemberNo,
    pageKey,
    category,
    headers ? new Headers(headers) : undefined,
  );
  const queryResult = useInfiniteQuery({
    queryKey,
    queryFn,
    enabled,
    initialPageParam: 1,
    getNextPageParam: (params) => {
      const nextPage = category === 'ENTERTAINMENT' ? params?.paging?.cursor || '' : (params?.paging?.current ?? 1) + 1;
      return !params?.paging?.isLast ? nextPage : undefined;
    },
    gcTime: 1000 * 60 * 30, // 30분 후 GC
    staleTime: 1000 * 60 * 30, //30분 후 캐시 만료
    meta: { persist: true },
  });
  const { data, isError, hasNextPage, fetchNextPage, isLoading, isFetchingNextPage, isRefetching } = queryResult;
  const isDataLoading = isLoading || isFetchingNextPage || isRefetching;

  useEffect(() => {
    if (inView && hasNextPage && !isError) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage, isError]);

  /**
   * 그룹으로 묶는 이유
   * @see {@link https://screen.yanolja.in/PWjtc1MxteprDs24}
   * 사진처럼 중간에 광고 배너가 들어가는 경우가 있을 경우 리스트 그룹핑을 다시 하므로 다음과 같은 형태로 변경
   * */
  /** Update: 2025-01-14
   * 첫번째 페이지 조회시 항상 OM 배너, 연관 검색어, 광고 목록 전체가 조회되므로 첫번째 페이지만 필터링하고 이후 페이지는 PRODUCT_ITEM 으로 그룹화
   */
  const firstPage =
    data?.pages[0]?.items.reduce<GroupedItems[]>((group, item) => {
      if (item.type === 'PRODUCT_ITEM' || item.type === 'BANNER_TYPE_PRODUCT_ITEM') {
        const lastGroup = group[group.length - 1];
        Array.isArray(lastGroup) ? lastGroup.push(item) : group.push([item]);
      } else if (
        item.type === 'RELATED_KEYWORD' ||
        item.type === 'ONSITE_MARKETING_BANNER' ||
        item.type === 'SECTION_GROUP_HEADER' ||
        item.type === 'SECTION_DIVIDER'
      ) {
        group.push(item);
      }
      return group;
    }, []) || [];

  // 첫번째 페이지 필터링 후 마지막 항목이 PRODUCT_ITEM 그룹인 경우 다른 페이지와의 그룹화을 위해 분리
  let lastProductItems = null;
  if (Array.isArray(firstPage[firstPage.length - 1])) {
    lastProductItems = firstPage.pop() as ProductUnitItem[];
  }

  const otherPages = [
    ...(lastProductItems ? lastProductItems : []),
    ...(data?.pages.slice(1).flatMap((page) => page.items) || []),
  ] as ProductUnitItem[];

  const filteredList = [...firstPage, ...(otherPages.length ? [otherPages] : [])];

  return {
    ...queryResult,
    isDataLoading,
    filteredList,
    lastItemRef: hasNextPage ? ref : noop,
    noProductResult: data?.pages[0]?.noProductResult,
    spellCorrection: data?.pages[0]?.spellCorrection,
    serverLogMeta: data?.pages[0]?.serverLogMeta || {},
    isFirstPageLoaded: !!data?.pages[0],
  };
};

export const useGlobalSearchListQuery = ({
  headers,
  params,
  hashedMemberNo,
  pageKey,
  enabled,
}: BaseSearchQueryParams) => {
  const { ref, inView } = useInView({ rootMargin: '500px 0px' });

  const { queryKey, queryFn } = queries.globalList.getGlobalListUniversalSearch(
    params,
    hashedMemberNo,
    pageKey,
    headers ? new Headers(headers) : undefined,
  );
  const queryResult = useInfiniteQuery({
    queryKey,
    queryFn,
    enabled,
    initialPageParam: 1,
    getNextPageParam: (params) => (!params?.paging?.isLast ? (params?.paging?.current ?? 1) + 1 : undefined),
    gcTime: 1000 * 60 * 30, // 30분 후 GC
    staleTime: 1000 * 60 * 30, //30분 후 캐시 만료
    meta: { persist: true },
  });
  const { data, isError, hasNextPage, fetchNextPage, isLoading, isFetchingNextPage, isRefetching } = queryResult;
  const isDataLoading = isLoading || isFetchingNextPage || isRefetching;

  useEffect(() => {
    if (inView && hasNextPage && !isError) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage, isError]);

  /**
   * 그룹으로 묶는 이유
   * @see {@link https://screen.yanolja.in/PWjtc1MxteprDs24}
   * 사진처럼 중간에 광고 배너가 들어가는 경우가 있을 경우 리스트 그룹핑을 다시 하므로 다음과 같은 형태로 변경
   * */
  /** Update: 2025-01-14
   * 첫번째 페이지 조회시 항상 OM 배너, 연관 검색어, 광고 목록 전체가 조회되므로 첫번째 페이지만 필터링하고 이후 페이지는 PRODUCT_ITEM 으로 그룹화
   */
  const firstPage =
    data?.pages[0]?.items.reduce<GroupedItems[]>((group, item) => {
      if (item.type === 'PRODUCT_ITEM' || item.type === 'BANNER_TYPE_PRODUCT_ITEM') {
        const lastGroup = group[group.length - 1];
        Array.isArray(lastGroup) ? lastGroup.push(item) : group.push([item]);
      } else if (
        item.type === 'RELATED_KEYWORD' ||
        item.type === 'ONSITE_MARKETING_BANNER' ||
        item.type === 'SECTION_GROUP_HEADER' ||
        item.type === 'SECTION_DIVIDER'
      ) {
        group.push(item);
      }
      return group;
    }, []) || [];

  // 첫번째 페이지 필터링 후 마지막 항목이 PRODUCT_ITEM 그룹인 경우 다른 페이지와의 그룹화을 위해 분리
  let lastProductItems = null;
  if (Array.isArray(firstPage[firstPage.length - 1])) {
    lastProductItems = firstPage.pop() as ProductUnitItem[];
  }

  const otherPages = [
    ...(lastProductItems ? lastProductItems : []),
    ...(data?.pages.slice(1).flatMap((page) => page.items) || []),
  ] as ProductUnitItem[];

  const filteredList = [...firstPage, ...(otherPages.length ? [otherPages] : [])];

  return {
    ...queryResult,
    isDataLoading,
    filteredList,
    lastItemRef: hasNextPage ? ref : noop,
    noProductResult: data?.pages[0]?.noProductResult,
    spellCorrection: data?.pages[0]?.spellCorrection,
    serverLogMeta: data?.pages[0]?.serverLogMeta || {},
    isFirstPageLoaded: !!data?.pages[0],
  };
};

/* 국내숙소 SRP 숙소 추천 조회 */
export const useRecommendedLocalAccommodation = ({
  keyword,
  checkInDate,
  checkOutDate,
  capacityAdults,
  childrenAges,
  enabled = true,
}: {
  keyword?: string;
  checkInDate: string;
  checkOutDate: string;
  capacityAdults: number;
  childrenAges: number[];
  enabled: boolean;
}) => {
  const { data, isLoading } = useQuery({
    ...queries.list.getRecommendationLocalAccommodation({
      checkInDate,
      checkOutDate,
      capacityAdults,
      childrenAges: childrenAges || [],
      keyword: keyword || '',
      recommendType: 'LOCAL_ACCOMMODATION_SRP_NO_RESULT',
    }),
    enabled: enabled && !!keyword,
  });

  const header = data?.items?.[0] as RecommendedListHeader;
  const items = (data?.items?.filter((item) => item.type !== 'RECOMMENDED_LIST_HEADER') || []) as ProductUnitItem[];

  return { header, items, isLoading };
};
/* 국내숙소 SRP 레저 추천 조회 */
export const useRecommendedLocalLeisure = ({
  keyword = '',
  enabled = true,
  recommendType = 'LOCAL_ACCOMMODATION_SRP',
}: {
  keyword?: string;
  enabled?: boolean;
  recommendType?: 'LOCAL_ACCOMMODATION_SRP' | 'LOCAL_LEISURE_SRP_NO_RESULT';
}) => {
  const { data, isLoading } = useQuery({
    ...queries.list.getRecommendationLocalLeisure({
      keyword: keyword || '',
      recommendType,
    }),
    enabled: enabled && !!keyword,
  });

  const header = data?.items?.[0] as RecommendedListHeader;
  const items = (data?.items?.filter((item) => item.type !== 'RECOMMENDED_LIST_HEADER') || []) as ProductUnitItem[];

  return { header, items, isLoading };
};

/** 통합 SRP 해외 지도 조회 */
export const useGlobalMapQuery = () => {
  const checkInDate = useAtomValue(checkInDateAtom);
  const checkOutDate = useAtomValue(checkOutDateAtom);
  const capacityAdults = useAtomValue(capacityAdultAtom);
  const childrenAges = useAtomValue(capacityChildAgesAtom);
  const globalCheckIOCapacity = useAtomValue(globalCheckIOCapacityAtom);
  const hashedMemberNo = useAtomValue(hashedMemberNoAtom);
  const sort = useAtomValue(searchFilterSortAtom);
  const location = useAtomValue(localLocationAtom);
  const keyword = useAtomValue(searchFilterKeywordAtom);
  const filters = useAtomValue(globalFilterParamsAtom);
  const selectedTabInfo = useAtomValue(selectedVerticalTabInfoAtom);

  const params = {
    keyword,
    filters: filters || [],
    category: selectedTabInfo?.key,
    sort: sort || 'RECOMMEND',
    userLocation: {
      latitude: Number(location?.latitude),
      longitude: Number(location?.longitude),
      locationType: location?.locationType,
      locationTime: location?.locationTime,
    },
    localAccommodation: {
      checkInDate,
      checkOutDate,
      capacityAdults,
      childrenAges,
    },
    globalAccommodation: {
      checkInDate: globalCheckIOCapacity.checkInDate,
      checkOutDate: globalCheckIOCapacity.checkOutDate,
      rooms: globalCheckIOCapacity.capacityRooms.map((room) => ({
        capacityAdults: room.adult,
        childrenAges: room.child,
      })),
    },
  } as UniversalSearchHeaderRequestBody;

  const { queryKey, queryFn } = queries.globalList.getGlobalListUniversalSearchMap(params, hashedMemberNo);

  const result = useInfiniteQuery({
    queryKey,
    queryFn,
    enabled: !!keyword,
    initialPageParam: 1,
    getNextPageParam: (params) => (!params?.paging?.isLast ? (params?.paging?.current ?? 1) + 1 : undefined),
    gcTime: 1000 * 60 * 30, //30분 후 GC
    staleTime: 1000 * 60 * 30, //30분 후 캐시 만료
    // meta: { persist: true }, // 데이터 복원이 필요없을것 같아서 임시 주석 처리
  });

  const { data, fetchNextPage, isLoading, isFetchingNextPage, isRefetching, isFetchNextPageError } = result;
  const isDataLoading = isLoading || isFetchingNextPage || isRefetching;
  const currentPaging = data?.pages[data.pages.length - 1]?.paging;
  const placeList = data?.pages?.flatMap((page) => page?.items ?? []) ?? [];

  return {
    ...result,
    paging: currentPaging,
    isDataLoading,
    placeList,
    serverLogMeta: data?.pages[0]?.serverLogMeta || {},
    fetchNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    isFirstPageLoaded: !!data?.pages[0],
  };
};
